/**
 * Shared top toolbar for any admin / data table: a "Showing X-Y of N" readout, a
 * rows-per-page select, and a prev/next pager. Drop it above (or into) any table to
 * get the platform-reports-style results bar.
 *
 * Stateless: the parent owns `page` / `perPage` and renders the slice. Two common
 * mounts:
 *   - BaseDataTable enhanced variant -> pass as `headerActions` (sits in the top band).
 *   - Any other table -> render it directly above the table, in a card header row.
 * Reference: `IntegrationEntityTable` (headerActions) and `FreshdeskStatus` (header row).
 */
import React from 'react'
import { IconButton, MenuItem, Select, Stack, Typography } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { poolwerxColors } from '../../theme/colors'

const DEFAULT_PER_PAGE_OPTIONS = [25, 50, 100]

interface TableResultsToolbarProps {
  /** Current page (1-based). */
  page: number
  perPage: number
  total: number
  onPageChange: (page: number) => void
  onPerPageChange: (perPage: number) => void
  perPageOptions?: number[]
}

const TableResultsToolbar: React.FC<TableResultsToolbarProps> = ({
  page,
  perPage,
  total,
  onPageChange,
  onPerPageChange,
  perPageOptions = DEFAULT_PER_PAGE_OPTIONS,
}) => {
  const pageCount = Math.max(1, Math.ceil(total / perPage))
  const current = Math.min(Math.max(1, page), pageCount)
  const start = total === 0 ? 0 : (current - 1) * perPage + 1
  const end = Math.min(current * perPage, total)

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ flexWrap: 'wrap', rowGap: 0.5 }}
    >
      {total > 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
        >
          Showing {start.toLocaleString()}-{end.toLocaleString()} of{' '}
          {total.toLocaleString()}
        </Typography>
      )}
      <Select
        size="small"
        value={perPage}
        onChange={(e) => onPerPageChange(Number(e.target.value))}
        renderValue={(v) => `${v} per page`}
        sx={{
          height: 32,
          fontSize: '0.8125rem',
          backgroundColor: 'background.paper',
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: poolwerxColors.teal,
          },
          '& .MuiSelect-select': { py: 0.5, pl: 1.25, pr: '28px !important' },
        }}
      >
        {perPageOptions.map((n) => (
          <MenuItem key={n} value={n}>
            {n} per page
          </MenuItem>
        ))}
      </Select>
      <Stack direction="row" alignItems="center">
        <IconButton
          size="small"
          aria-label="Previous page"
          onClick={() => onPageChange(current - 1)}
          disabled={current <= 1}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ whiteSpace: 'nowrap', px: 0.5, fontVariantNumeric: 'tabular-nums' }}
        >
          Page {current} of {pageCount}
        </Typography>
        <IconButton
          size="small"
          aria-label="Next page"
          onClick={() => onPageChange(current + 1)}
          disabled={current >= pageCount}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Stack>
  )
}

export default TableResultsToolbar
